import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import type { components } from "@/api/types";
import { robotColor } from "@/entities/robot";
import { ROUTES } from "@/shared/config";
import { formatDay, formatMoney } from "@/shared/lib";
import { Button, RollingNumber } from "@/shared/ui";
import { ScreenState } from "@/widgets/screen-state";

type PurchaseResult = components["schemas"]["PurchaseResult"];

/**
 * Успех. Figma: `Robots · Launch Success` (22:259).
 *
 * Данные приходят в `location.state` из ответа на покупку: `GET`-ручки для
 * заявки в API нет, перезапросить результат нечем. При перезагрузке страницы
 * состояние теряется — тогда показывается объяснение со ссылкой на «мои
 * роботы», а не пустой экран.
 *
 * Здесь же единственное место, где можно честно показать баланс после
 * списания: `balance_after` приходит с бэкенда готовым.
 *
 * Единственный экран приложения, который празднует (FE-14): волна по кольцу,
 * салют в цвете купленного робота, тактильная фанфара и докатывающийся
 * одометр баланса. Всё это включает один проп `celebrate` у `ScreenState`.
 */
export function BuySuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const state: unknown = location.state;
  const result =
    state && typeof state === "object" && "result" in state
      ? (state.result as PurchaseResult)
      : undefined;

  if (!result) {
    return (
      <ScreenState
        tone="info"
        icon="checkFat"
        title={t("buy.success.title")}
        body={t("buy.success.lostState")}
        action={{ label: t("buy.success.toMyRobots"), onClick: () => void navigate(ROUTES.myRobots) }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 px-5 py-6">
      <ScreenState
        celebrate
        accent={robotColor(result.robot)}
        tone="profit"
        icon="checkFat"
        title={t("buy.success.title")}
        body={t("buy.success.body", { name: result.robot.name })}
      />

      <section className="animate-rise-in flex flex-col gap-[9px] rounded-[20px] border border-border-subtle bg-background-elevated p-4">
        <p className="text-micro font-semibold uppercase text-text-tertiary">{t("buy.success.now")}</p>
        <p className="text-body font-semibold text-text-primary">{t("buy.success.analysing")}</p>
        <p className="text-micro font-normal text-text-secondary">
          {t("buy.success.balanceAfter")}{" "}
          <RollingNumber tick value={formatMoney(result.balance_after, { suffix: "USDT" })} />
        </p>
        {/*
          Дата подключения средств (ТЗ §19). Робот, запущенный сегодня, за
          сегодня не начисляет — обещать мгновенную работу нельзя.
        */}
        <p className="text-micro font-normal text-text-secondary">
          {t("buy.success.effectiveFrom", { date: formatDay(result.effective_from) })}
        </p>
      </section>

      <Button
        size="cta"
        block
        onClick={() => void navigate(ROUTES.myRobot(result.user_robot_id))}
      >
        {t("buy.success.openRobot")}
      </Button>
    </div>
  );
}
